import './style.css'
import * as dat from 'dat.gui'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import firefliesVertexShader from './shaders/fireflies/vertex.glsl'
import firefliesFragmentShader from './shaders/fireflies/fragment.glsl'
import portalVertexShader from './shaders/portal/vertex.glsl'
import portalFragmentShader from './shaders/portal/fragment.glsl'
import { gsap } from 'gsap'

/**
 * Base
 */
//Debug
const debugObject = {}
const gui = new dat.GUI({
    width: 400,
})

dat.GUI.toggleHide();

//Canvas
const canvas = document.querySelector('canvas.webgl')

//Scene
const scene = new THREE.Scene()

/**
 * Overlay
 */
const overlayGeometry = new THREE.PlaneBufferGeometry(2,2,1,1);
const overlayMaterial = new THREE.ShaderMaterial({
    transparent:true,
    uniforms:{
        uAlpha: {value:1}
    },
    vertexShader:`
    void main(){
        gl_Position = vec4(position, 1.0);
    }
    `,
    fragmentShader:`
    uniform float uAlpha;

    void main(){
        gl_FragColor =vec4(0.0, 0.0, 0.0, uAlpha);
    }
    `
})
const overlay = new THREE.Mesh(overlayGeometry, overlayMaterial)
scene.add(overlay)

/**
 * Loaders
 */
//Texture loader
const textureLoader = new THREE.TextureLoader()

//Draco loader
const dracoLoader = new DRACOLoader()
dracoLoader.setDecoderPath('../draco/')

//GLTF loader
const loadingbar = document.querySelector('.loading-bar')
const loadingManager = new THREE.LoadingManager(
    //Loaded
    () =>{
        gsap.delayedCall(0.5,()=>{
            gsap.to(overlayMaterial.uniforms.uAlpha, {duration:3, value:0})
            loadingbar.classList.add('ended')
            loadingbar.style.transform=''
        })
    },
    //Progress
    (itemUrl, itemLoaded, itemsTotal) =>{
        const progress = itemLoaded / itemsTotal
        loadingbar.style.transform = `scaleX(${progress})`
    }
)
const gltfLoader = new GLTFLoader(loadingManager)
const cubeTextureLoader = new THREE.CubeTextureLoader(loadingManager)
gltfLoader.setDRACOLoader(dracoLoader)

/**
 * Textures
 */
const bakedTexture = textureLoader.load('baked.jpg')
bakedTexture.flipY = false
bakedTexture.encoding = THREE.sRGBEncoding

/**
 * Materials
 */
//Baked material
const bakedMaterial = new THREE.MeshBasicMaterial({ map: bakedTexture })

debugObject.couleurPortailDebut = '#00004f'
debugObject.couleurPortailFin = '#5c77fa'

gui.addColor(debugObject, 'couleurPortailDebut').onChange(() => {
        portalLightMaterial.uniforms.uColorStart.value.set(debugObject.couleurPortailDebut)
});

gui.addColor(debugObject, 'couleurPortailFin').onChange(() =>{
        portalLightMaterial.uniforms.uColorEnd.value.set(debugObject.couleurPortailFin)
});

//Portal light material
//const portalLightMaterial = new THREE.MeshBasicMaterial({color: 0x5c77fa, side: THREE.DoubleSide})
const portalLightMaterial = new THREE.ShaderMaterial({
    uniforms:{
        uTime: { value: 0 },
        uColorStart: { value: new THREE.Color(debugObject.couleurPortailDebut) },
        uColorEnd: { value: new THREE.Color(debugObject.couleurPortailFin) }
    },
    vertexShader: portalVertexShader,
    fragmentShader: portalFragmentShader
})
portalLightMaterial.side = THREE.DoubleSide;

//Pole light material
const poleLightMaterial = new THREE.MeshBasicMaterial({ color: 0xffffe5 })

/**
 * Model
 */
gltfLoader.load(
    'portal.glb',
    (gltf) =>
    {
        const bakedMesh = gltf.scene.children.find(child => child.name === 'baked')
        const portalLightMesh = gltf.scene.children.find(child => child.name === 'portalLight')
        const poleLightAMesh = gltf.scene.children.find(child => child.name === 'poleLightA')
        const poleLightBMesh = gltf.scene.children.find(child => child.name === 'poleLightB')

        bakedMesh.material = bakedMaterial
        portalLightMesh.material = portalLightMaterial
        poleLightAMesh.material = poleLightMaterial
        poleLightBMesh.material = poleLightMaterial

        scene.add(gltf.scene)
    }
)

/**
 * Fireflies
 */
//Geometry
const firefliesGeometry = new THREE.BufferGeometry()
const firefliesCount = 30
const positionArray = new Float32Array(firefliesCount * 3)
const scaleArray = new Float32Array(firefliesCount)

for(let i = 0; i< firefliesCount; i++){
    positionArray[i*3+0] = (Math.random() - 0.5) * 4
    positionArray[i*3+1] = Math.random() * 1.5 + 0.2
    positionArray[i*3+2] = (Math.random() - 0.5) * 4

    scaleArray[i] = Math.random()
}

firefliesGeometry.setAttribute('position', new THREE.BufferAttribute(positionArray, 3))
firefliesGeometry.setAttribute('aScale', new THREE.BufferAttribute(scaleArray, 1))
//Material 
const firefliesMaterial = new THREE.ShaderMaterial({
    uniforms:{
        uTime: {value: 0 },
        uPixelRatio: { value: Math.min(window.devicePixelRatio, 2)},
        uSize: { value : 100 }
    },
    vertexShader: firefliesVertexShader,
    fragmentShader: firefliesFragmentShader,
    blending: THREE.AdditiveBlending,
    depthWrite: false
})

gui.add(firefliesMaterial.uniforms.uSize, 'value').min(0).max(500).step(1).name('fireflies')

//Points
const fireflies = new THREE.Points(firefliesGeometry, firefliesMaterial)
console.log(fireflies)
scene.add(fireflies)



/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}

window.addEventListener('resize', () =>
{
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    // Update camera
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    
    //Update fireflies
    firefliesMaterial.uniforms.uPixelRatio.value = Math.min(window.devicePixelRatio, 2)
})

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(45, sizes.width / sizes.height, 0.1, 100)
camera.position.x = 4
camera.position.y = 2
camera.position.z = 4
scene.add(camera)

// Controls
const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true
controls.minPolarAngle = 0;
controls.maxPolarAngle =  Math.PI * 0.5 - 0.1;

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true
})
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
renderer.outputEncoding = THREE.sRGBEncoding

debugObject.couleurFond = "#1a1b27"
renderer.setClearColor(debugObject.couleurFond)
gui.addColor(debugObject, 'couleurFond').onChange(() =>{
renderer.setClearColor(debugObject.couleurFond)
})
/**
 * Animate
 */
const clock = new THREE.Clock()

const tick = () =>
{
    const elapsedTime = clock.getElapsedTime()

    //Update materials
    firefliesMaterial.uniforms.uTime.value = elapsedTime;
    portalLightMaterial.uniforms.uTime.value = elapsedTime;

    // Update controls
    controls.update()

    // Render
    renderer.render(scene, camera)

    // Call tick again on the next frame
    window.requestAnimationFrame(tick)
}

tick()